import {
    memoize,
    transform,
    isNil,
    mapValues,
    find,
    toLower,
    trim,
    isEmpty,
    filter,
    startsWith,
    includes,
    reduce,
} from 'lodash';
import { getOr, range } from 'lodash/fp';
import RBush from 'rbush';
import { mapCustomerClassifications } from '@mews-commander/utils';
import {
    EnhancedResource,
    ReservationResourceUse,
    ResourceUsePlaceholder,
    ResourceUseResource,
} from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { LINE_HEIGHT, RESOURCE_WIDTH, DATE_LEGEND_HEIGHT } from '../constants';
import { round } from '../utils/mathUtils';
import { CalendarEventExtended, CursorPosition } from '../types';
import { NewReservationInfo } from '../../newReservation/components/NewReservationPanel/types';

const memoCustomerClassifications = memoize(mapCustomerClassifications);

export default function Model(state: $TSFixMe, handleHeightChanged: $TSFixMe) {
    const resourcesSearchTree = new RBush<ResourceUseResource>();
    const resourceUsesSearchTree = new RBush<ReservationResourceUse>();
    const eventSearchTree = new RBush<CalendarEventExtended>();
    /**
     * Placeholder for `+` new reservation
     */
    let newResourceUsePlaceholder: ResourceUsePlaceholder | null = null;
    /**
     * Placeholder for already confirmed new reservation placeholder.
     * Is set after newResourceUsePlaceholder is clicked on
     */
    let confirmedResourceUsePlaceholder: NewReservationInfo | null = null;

    let customers = {};

    let visibleResourceUses: $TSFixMe = [];

    let visibleEventsByDayIndex = {};

    let visibleResources: Array<ResourceUseResource> = [];
    let visibleRootResourceCategoryTypes: $TSFixMe = [];
    let resourceLookUp: Record<string, EnhancedResource> = {};
    let resourcesMaxDepth = 0;
    let resourcesMaxY = 0;

    return {
        setResourceUsePlaceholder: (placeholder: ResourceUsePlaceholder | null) => {
            newResourceUsePlaceholder = placeholder;
        },
        setConfirmedResourceUsePlaceholder: (placeholder: NewReservationInfo | null) => {
            confirmedResourceUsePlaceholder = placeholder;
        },
        updateResources({ maxDepth, leafCount, resources, lookup }: $TSFixMe) {
            const { canvasHeight } = state;
            resourcesMaxDepth = maxDepth;
            resourceLookUp = lookup;
            resourcesMaxY = leafCount;
            resourcesSearchTree.clear();
            resourcesSearchTree.load(resources);

            // update scrollable height of zoom to include all resources
            const heightPixels = leafCount * LINE_HEIGHT + DATE_LEGEND_HEIGHT;
            handleHeightChanged(heightPixels, canvasHeight);
        },

        updateResourceUses(resourceUses: $TSFixMe, resourcesLookUp: $TSFixMe) {
            const assignedResourceUses = transform(
                resourceUses,
                (acc, use) => {
                    const resource = resourcesLookUp[(use as $TSFixMe).assignedResourceId];
                    if ((use as $TSFixMe).isReservation) {
                        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                        const customer = customers[(use as $TSFixMe).customerId];
                        if (isNil(customer)) {
                            return acc;
                        }
                        (use as $TSFixMe).customer = customer;
                    }
                    if (resource) {
                        (use as $TSFixMe).minY = resource.minY;
                        (use as $TSFixMe).maxY = resource.maxY;
                        (use as $TSFixMe).resource = resource;
                        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'never'.
                        acc.push(use);
                    }
                    return acc;
                },
                []
            );
            resourceUsesSearchTree.clear();
            resourceUsesSearchTree.load(assignedResourceUses);
        },

        updateCustomers(data: $TSFixMe) {
            customers = mapValues(data, (customer) => ({
                ...customer,
                classificationsDecoded: memoCustomerClassifications(customer.classifications),
            }));
        },

        updateEvents(events: CalendarEventExtended[] = []) {
            eventSearchTree.clear();
            eventSearchTree.load(events);
        },

        findResourceUseById(useId: $TSFixMe) {
            return find(resourceUsesSearchTree.all(), (use) => use.id === useId);
        },

        findResourceByName(searchToken: $TSFixMe) {
            const token = toLower(trim(searchToken));
            if (isEmpty(token)) {
                return false;
            }

            const resources = filter(
                resourceLookUp,
                (resource) =>
                    startsWith(toLower((resource as $TSFixMe).name), token) ||
                    includes(toLower((resource as $TSFixMe).resourceCategoryName), token) ||
                    includes(toLower((resource as $TSFixMe).resourceCategoryShortName), token)
            );

            if (isEmpty(resources)) {
                return false;
            }

            return resources[0];
        },

        findResourceById(id: string): EnhancedResource | undefined {
            return resourceLookUp[id];
        },

        getResourceUsePlaceholder: () => newResourceUsePlaceholder,

        getConfirmedResourceUsePlaceholder: () => confirmedResourceUsePlaceholder,

        getResourceUseAtPosition(position: CursorPosition) {
            return getResourceUsesAtPosition(position)[0];
        },

        getResourceUsesAtPosition,

        getResources(minY: number, maxY: number) {
            return searchResources(0, resourcesMaxDepth, minY, maxY);
        },
        getResourceAtPosition(position: CursorPosition) {
            const resourceWidth = round(RESOURCE_WIDTH * state.scale);
            const x = (position.x - LINE_HEIGHT) / resourceWidth;
            const y = state.yAxis.invert(position.y - DATE_LEGEND_HEIGHT);
            return searchResources(x, x, y, y)[0];
        },

        getEventsAtPosition(position: CursorPosition): CalendarEventExtended[] {
            const xIndex = round(state.xAxis.invert(position.x));
            return getOr([], xIndex)(visibleEventsByDayIndex);
        },

        getResourceAtY(position: CursorPosition) {
            const y = state.yAxis.invert(position.y - DATE_LEGEND_HEIGHT);
            const found = searchResources(0, resourcesMaxDepth, y, y);
            return filter(found, (resource) => resource.isLeaf)[0];
        },

        getHighlightedResources(position: CursorPosition) {
            const y = state.yAxis.invert(position.y - DATE_LEGEND_HEIGHT);
            return searchResources(0, resourcesMaxDepth, y, y);
        },

        viewportChanged() {
            visibleResources = resourcesSearchTree.search({
                minY: state.viewport.minY - 2 / state.scale,
                maxY: (state.viewport.maxY as number) + 2,
                minX: 0,
                maxX: resourcesMaxDepth,
            });
            visibleRootResourceCategoryTypes = reduce(
                visibleResources,
                (acc, resource) => {
                    if (!resource.isRoot) {
                        return acc;
                    }

                    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                    if (acc[resource.resourceCategoryId]) {
                        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                        if (resource.minY < acc[resource.resourceCategoryId].minY) {
                            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                            acc[resource.resourceCategoryId].minY = resource.minY;
                        }
                        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                        if (resource.maxY > acc[resource.resourceCategoryId].maxY) {
                            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                            acc[resource.resourceCategoryId].maxY = resource.maxY;
                        }
                    } else {
                        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                        acc[resource.resourceCategoryId] = {
                            minY: resource.minY,
                            maxY: resource.maxY,
                            id: resource.resourceCategoryId,
                            resourceCategoryShortName: resource.resourceCategoryShortName,
                            resourceCategoryName: resource.resourceCategoryName,
                        };
                    }
                    return acc;
                },
                {}
            );

            visibleResourceUses = resourceUsesSearchTree.search({
                minY: state.viewport.minY,
                maxY: (state.viewport.maxY as number) + 2,
                minX: state.viewport.minX,
                maxX: state.viewport.maxX,
            });

            const events: CalendarEventExtended[] = eventSearchTree.search({
                minY: 0,
                maxY: 0,
                minX: state.viewport.minX,
                maxX: state.viewport.maxX,
            });

            visibleEventsByDayIndex = events.reduce(
                (acc, event: CalendarEventExtended) => {
                    const indexes = range(event.minX, event.maxX);
                    indexes.forEach((dayIndex: number) => {
                        acc[dayIndex] = acc[dayIndex] ?? [];
                        acc[dayIndex].push(event);
                    });
                    return acc;
                },
                {} as Record<number, CalendarEventExtended[]>
            );
        },
        getVisibleResources() {
            return visibleResources;
        },

        getVisibleEventsByDayIndex() {
            return visibleEventsByDayIndex;
        },

        getVisibleRootResourceCategoryTypes() {
            return visibleRootResourceCategoryTypes;
        },

        getMaxResourceDepth() {
            return resourcesMaxDepth;
        },

        getResourcesMaxY() {
            return resourcesMaxY;
        },

        getVisibleResourceUses() {
            return visibleResourceUses;
        },
    };

    function searchResources(minX: $TSFixMe, maxX: $TSFixMe, minY: $TSFixMe, maxY: $TSFixMe) {
        return resourcesSearchTree.search({
            minX,
            maxX,
            minY,
            maxY,
        });
    }

    function getResourceUsesAtPosition(position: CursorPosition) {
        const x = state.xAxis.invert(position.x);
        const y = state.yAxis.invert(position.y - DATE_LEGEND_HEIGHT);
        return resourceUsesSearchTree.search({
            minX: x,
            maxX: x,
            minY: y,
            maxY: y,
        });
    }
}
